import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, MessageCircle } from "lucide-react";
import { MaskLine, Reveal, FadeIn, Overline } from "@/components/motion";
import { Marquee } from "@/components/Layout";
import { WA_URL } from "@/lib/site";

function Monogram({ name, featured = false }) {
  const initials = name
    .replace(/Dott\.?|ssa/g, "")
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("");
  const size = featured ? "w-24 h-24" : "w-16 h-16";
  return (
    <div className={`relative ${size} shrink-0`} aria-hidden="true">
      <div className="absolute inset-0 rounded-full border-2 border-indaco transition-transform duration-500 group-hover:scale-110" />
      <div className="absolute inset-0 rounded-full border-2 border-arancio translate-x-2.5 transition-transform duration-500 group-hover:translate-x-4" />
      <span className="absolute inset-0 flex items-center justify-center font-sora font-bold text-indaco text-[15px] md:text-base tracking-wide">
        {initials}
      </span>
    </div>
  );
}

const PROOF_ITEMS = [
  "Università di Torino",
  "Ospedale Molinette",
  "Centro Regionale Disturbi del Sonno",
  "Harvard Medical School",
];

const DOCTORS = [
  {
    name: "Dott. Enrico Cavallo",
    role: "NEUROLOGO · FONDATORE E DIRETTORE SANITARIO",
    bio: "Fondatore di Synapses. Neurologo, si è laureato e specializzato con lode all'Università di Torino, con una tesi sulla SLA poi pubblicata. Per anni ha assistito a domicilio, come volontario, le persone con questa malattia. Oggi esegue di persona elettromiografie, EEG e polisonnografie, ed è membro della Società Italiana di Neurofisiologia Clinica. Ha partecipato a studi clinici multicentrici nazionali e internazionali. Ha maturato la sua esperienza tra ospedale, università e ricerca.",
    profile: [
      "Laurea con lode in Medicina e Chirurgia, Università di Torino (2006), con una tesi sulla SLA oggetto di pubblicazione scientifica",
      "Specializzazione con lode in Neurologia, Università di Torino (2012), con una tesi sul sonno nella malattia di Parkinson avanzata",
      "Attività clinica e di ricerca al Centro di Riferimento Regionale per i Disturbi del Sonno, Dipartimento di Neuroscienze dell'Università di Torino",
      "Medico volontario per l'assistenza domiciliare delle persone con SLA, con APASLA",
      "Assistente medico alla Casa di Cura Villa Grazia di San Carlo Canavese, con attività ambulatoriale ed elettromiografica",
      "Membro della Società Italiana di Neurofisiologia Clinica (SINC)",
      "Partecipazione a studi clinici multicentrici nazionali e internazionali; autore di pubblicazioni scientifiche",
      "Lingue: italiano, spagnolo",
      "Iscrizione all'Albo dei Medici n. 20920",
    ],
    featured: true,
  },
  {
    name: "Dott. Luca Benedetto Sicuro",
    role: "NEUROLOGO",
    bio: "Oltre trent'anni di neurologia in ospedale, di cui sedici nella Terapia del Dolore della Città della Salute di Torino. Conosce da vicino il dolore che non passa. È autore di oltre 90 pubblicazioni scientifiche.",
    profile: [
      "Laurea con lode in Medicina e Chirurgia, Università di Torino (1980)",
      "Specializzazione in Neurologia con lode e dignità di stampa, Università di Torino (1984)",
      "Neurologia all'Ospedale E. Agnelli di Pinerolo (1982–1983), all'Ospedale Maria Vittoria di Torino (1984–1987) e all'Ospedale San Giovanni Battista – Molinette di Torino (1987–2001)",
      "Struttura Complessa di Terapia del Dolore e Cure Palliative, AOU Città della Salute e della Scienza di Torino (2001–2017)",
      "Oltre 90 pubblicazioni scientifiche",
    ],
  },
  {
    name: "Dott.ssa Elena Peila",
    role: "NEUROLOGA · ESPERTA IN MEDICINA DEL SONNO",
    bio: "È Esperta in Medicina del Sonno, con certificazione AIMS dal 2008. Ha lavorato al Centro di Riferimento Regionale per i Disturbi del Sonno di Torino e ha guidato il Centro di Medicina del Sonno dell'ASL CN2 Alba-Bra. Si è perfezionata in neurostimolazione alla Harvard Medical School di Boston.",
    profile: [
      "Laurea con lode in Medicina e Chirurgia, Università di Torino (2005), con una tesi sul sonno nella malattia di Parkinson",
      "Specializzazione in Neurologia, Università di Torino (2011), con una tesi su sonno ed emicrania",
      "Esperta in Medicina del Sonno, certificazione AIMS – Associazione Italiana di Medicina del Sonno (2008)",
      "Dottorato di Ricerca in Neuroscienze, Università di Torino, sulle tecniche di neurostimolazione cerebrale",
      "Formazione in neurostimolazione al Berenson-Allen Center for Noninvasive Brain Stimulation, Harvard Medical School (Boston)",
      "Centro di Riferimento Regionale per i Disturbi del Sonno, AOU Città della Salute e della Scienza di Torino",
      "Responsabile del Centro di Medicina del Sonno, ASL CN2 Alba-Bra",
      "Collaborazioni con il Laboratorio di Neurofisiologia e Neuroimaging funzionale del Dipartimento di Neuroscienze di Torino e con il Centro di Terapia del Dolore e Cure Palliative della Città della Salute",
      "Membro di società scientifiche italiane ed europee; autrice di pubblicazioni su riviste indicizzate",
    ],
  },
  {
    name: "Dott.ssa Antonella Tribolo",
    role: "NEUROLOGA",
    bio: "Oltre quarant'anni di esperienza clinica, sempre in ospedale. Dal 2005 è dirigente medico alla Neurologia delle Molinette di Torino, dove segue anche l'ambulatorio del Centro di Medicina del Sonno. È relatrice a numerosi convegni di Medicina del Sonno.",
    profile: [
      "Laurea in Medicina e Chirurgia, Università di Torino (1984)",
      "Specializzazione in Neurologia, Clinica Neurologica II dell'Università di Torino (1989)",
      "Assistente e dirigente medico di Neurologia all'Ospedale Civile di Asti, alla Divisione Universitaria di Neurologia dell'AO San Luigi di Orbassano e all'AO Maggiore della Carità di Novara",
      "Dal 2005 dirigente medico a tempo pieno alla Neurologia dell'AOU Città della Salute e della Scienza di Torino – Presidio Molinette, con attività clinica e di ricerca in neurofisiologia",
      "Attività ambulatoriale al Centro di Medicina del Sonno e all'ambulatorio di terapie con tossina botulinica",
      "Membro della LIMPE (Lega Italiana per la Malattia di Parkinson e i Disturbi del Movimento) e dell'AIMS",
      "Relatrice a convegni su Medicina del Sonno e uso della tossina botulinica in Neurologia",
    ],
  },
  {
    name: "Dott. Giorgio Capoccitti",
    role: "MEDICO SPECIALISTA IN NEUROFISIOPATOLOGIA",
    bio: "È lo specialista degli esami che studiano nervi e muscoli: l'elettromiografia e l'elettroneurografia, che misura come conducono i nervi. Dal 2019 è dirigente medico alla Neurologia dell'Ospedale Parini di Aosta. Si occupa di compressioni dei nervi, come il tunnel carpale, e di malattie di nervi e muscoli, anche rare.",
    profile: [
      "Laurea in Medicina e Chirurgia, Università di Siena (2010)",
      "Specializzazione in Neurofisiopatologia, Sapienza Università di Roma (2016)",
      "Dal 2019 dirigente medico di I livello alla Neurologia dell'Ospedale U. Parini di Aosta",
      "Diagnostica delle malattie del nervo periferico, delle radicolopatie, delle malattie del motoneurone (SLA, PMA, PLS), delle neuropatie disimmuni e delle miopatie",
      "Elettromiografia ed elettroneurografia per sindromi da compressione nervosa come il tunnel carpale, miastenia gravis, miotonie e nevralgie craniche",
    ],
  },
];

const TECHNICIANS = [
  {
    name: "Alice Robert",
    role: "Tecnico di Neurofisiopatologia",
    areas: "Neurofisiologia, medicina del sonno, epilessia",
    bio: "Ha lavorato per molti anni presso i servizi di Neurofisiologia dell'Ospedale Molinette di Torino, in particolare presso il Centro di Medicina del Sonno e il Centro Epilessia.",
  },
  {
    name: "Simone Vaccarella",
    role: "Tecnico di Neurofisiopatologia",
    areas: "Elettromiografia, elettroencefalografia, esami del sonno",
    bio: "Esegue e assiste gli esami strumentali del centro.",
  },
];

function DoctorBlock({ d, i }) {
  const [open, setOpen] = useState(false);
  return (
    <section
      className={`${d.featured || i % 2 === 1 ? "bg-crema-warm" : "bg-crema"} py-16 lg:py-24`}
      data-testid={`doctor-block-${i}`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <Reveal>
            <div className="group lg:sticky lg:top-32">
              <Monogram name={d.name} featured />
              <h3 className="mt-8 font-sora font-bold tracking-tight text-2xl md:text-3xl text-ink">
                {d.name}
              </h3>
              <p className="mt-2 text-sm uppercase tracking-[0.18em] text-arancio font-semibold">
                {d.role}
              </p>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-8 lg:pt-4">
          <Reveal delay={0.1}>
            <p className="text-[17px] md:text-lg text-ink/80 leading-relaxed max-w-2xl">{d.bio}</p>
          </Reveal>
          <Reveal delay={0.18}>
            <button
              onClick={() => setOpen((o) => !o)}
              className="mt-8 inline-flex items-center gap-2 text-indaco font-semibold text-[15px] md:text-base group"
              data-testid={`profile-toggle-${i}`}
              aria-expanded={open}
            >
              {open ? "Chiudi il profilo" : "Leggi il profilo completo"}
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
              />
            </button>
            <AnimatePresence initial={false}>
              {open && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                  data-testid={`profile-list-${i}`}
                >
                  <ul className="mt-6 space-y-3 max-w-2xl border-l-2 border-indaco/20 pl-6 pb-2">
                    {d.profile.map((item, j) => (
                      <li key={j} className="text-[15px] md:text-base text-ink/75 leading-relaxed flex gap-3">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-arancio shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}
            </AnimatePresence>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function TechnicianCard({ t, i }) {
  return (
    <Reveal delay={0.08 * i}>
      <div
        className="group bg-crema border border-ink/5 rounded-lg p-8 lg:p-10 h-full transition-colors duration-300 hover:border-indaco/30"
        data-testid={`tech-card-${i}`}
      >
        <Monogram name={t.name} />
        <h3 className="mt-8 font-sora font-bold tracking-tight text-2xl text-ink transition-colors duration-300 group-hover:text-indaco">
          {t.name}
        </h3>
        <p className="mt-1 text-sm uppercase tracking-[0.18em] text-arancio font-semibold">{t.role}</p>
        <p className="mt-4 text-[15px] md:text-base font-medium text-ink/80">{t.areas}</p>
        <p className="mt-3 text-[15px] md:text-base text-viole leading-relaxed">{t.bio}</p>
      </div>
    </Reveal>
  );
}

export default function Team() {
  return (
    <>
      <section className="bg-indaco-deep text-crema pt-40 pb-24 lg:pt-52 lg:pb-32 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute -right-40 -top-10 w-[440px] h-[440px] rounded-full border border-crema/10" />
          <div className="absolute -right-16 top-24 w-[440px] h-[440px] rounded-full border border-arancio/20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <FadeIn>
            <Overline light>Il team</Overline>
          </FadeIn>
          <h1 className="mt-8 font-sora font-extrabold tracking-tighter text-4xl md:text-5xl lg:text-6xl leading-[1.05]" data-testid="team-title">
            <MaskLine delay={0.15}>Sette persone, un solo modo</MaskLine>
            <MaskLine delay={0.3}>
              di lavorare: <span className="text-arancio">ascoltarti</span>.
            </MaskLine>
          </h1>
          <FadeIn delay={0.6}>
            <p className="mt-8 max-w-2xl text-crema/70 text-lg leading-relaxed">
              5 medici specialisti e 2 tecnici di neurofisiopatologia, con esperienza
              all'Ospedale Molinette e all'Università di Torino. Nessuno di loro lavora da solo:
              si confrontano su ogni caso complesso.
            </p>
          </FadeIn>
        </div>
      </section>

      <div className="bg-crema" data-testid="proof-strip">
        <Marquee items={PROOF_ITEMS} />
      </div>

      <section className="bg-crema pt-24 lg:pt-36 pb-4" data-testid="le-persone">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <Overline>Le persone</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-sora font-bold tracking-tighter text-3xl md:text-5xl text-ink max-w-3xl">
              Percorsi diversi, stesso metodo: ascoltarti.
            </h2>
          </Reveal>
        </div>
      </section>

      {DOCTORS.map((d, i) => (
        <DoctorBlock key={d.name} d={d} i={i} />
      ))}

      <section className="bg-crema py-24 lg:py-32" data-testid="tecnici-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-5">
          {TECHNICIANS.map((t, i) => (
            <TechnicianCard key={t.name} t={t} i={i} />
          ))}
        </div>
      </section>

      <section className="bg-crema-warm py-24 lg:py-32" data-testid="team-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-sora font-bold tracking-tighter text-3xl md:text-5xl text-ink">
              Un medico di riferimento, anche la volta dopo.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 text-viole text-lg leading-relaxed">
              Qui sai chi ti segue, e lo ritrovi quando torni. Raccontaci cosa ti succede:
              ti indirizziamo noi allo specialista più adatto.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/prenota"
                className="group inline-flex items-center gap-3 rounded-full bg-indaco px-8 py-4 text-[15px] md:text-base font-semibold text-crema transition-colors duration-300 hover:bg-indaco-deep"
                data-testid="team-cta-prenota"
              >
                Richiedi un appuntamento →
              </Link>
              <a
                href={WA_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 rounded-full border border-ink/20 px-8 py-4 text-[15px] md:text-base font-semibold text-ink transition-colors duration-300 hover:border-indaco hover:text-indaco"
                data-testid="team-cta-whatsapp"
              >
                <MessageCircle className="w-4 h-4" />
                Chiedi su WhatsApp
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.35}>
            <p className="mt-6 text-[15px] md:text-base text-viole">Ti rispondiamo noi entro 24 ore. Senza impegno.</p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
